import "./App.css";
import { useState, useEffect, useContext, createContext } from "react";
import NiceModal from "@ebay/nice-modal-react";
import {
  CheckIcon,
  LockClosedIcon,
  TrashIcon,
  PencilIcon,
  ChatBubbleBottomCenterIcon,
  CalendarIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { TrashIcon as TrashIconMicro } from "@heroicons/react/24/outline";
import { CalendarDaysIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  Routes,
  Route,
  NavLink,
  Outlet,
  Navigate,
  useOutletContext,
  useNavigate,
  createBrowserRouter,
  createRoutesFromElements,
  defer,
} from "react-router-dom";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { UserList, getTeam } from "./components/userlist";
import { Settings } from "./components/Settings";
import { Help } from "./components/Help";
import { RessourceFilter } from "./components/RessourceFilter";
import BookingForm from "./components/BookingForm";
import AddTimeslotDialog from "./components/AddTimeslotDialog";
import PasswordEditor from "./components/PasswordEditor";
import Registration from "./components/Registration";
import PasswordReset from "./components/PasswordReset";
import LoginScreen from "./components/Login";
import { AuthLayout } from "./components/AuthLayout";

import sendFormData from "./utils/sendFormData";
import EmptyTimeslots from "./components/EmptyTimeslots";
import { useAuth } from "./utils/useAuth";
import { ProtectedLayout } from "./components/ProtectedLayout";
import { ProjectsSelector } from "./components/ProjectsSelector";
import ProjectEditor from "./components/ProjectEditor";

const navigation = [
  { name: "Dashboard", href: "/admin", admin: false },
  { name: "Einstellungen", href: "/admin/settings", admin: true },
  { name: "Team", href: "/admin/team", admin: true },
  { name: "Hilfe", href: "/admin/help", admin: true },
];

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const UserContext = createContext(null);

const postFormdata = async function (form, url) {
  const response = await fetch(form.action, {
    method: "post",
    body: new URLSearchParams(new FormData(form)),
  });
  if (!response.ok) {
    alert("Es ist ein Fehler beim Speichern ihrer Eingabe aufgetreten");
  }
  return response;
};

async function deleteTimeslot(timeslot_id) {
  const response = await fetch("/api/admin/timeslot/" + timeslot_id, {
    method: "DELETE",
  });
  if (!response.ok) {
    alert("Es ist ein Fehler beim Löschen des Termins aufgetreten.");
    const errorData = await response.json();
    throw new Error(errorData.message);
  }
  return response;
}

function RessourceComment({ slot_id, comment }) {
  let [showEditor, setShowEditor] = useState(false);
  let [newComment, setComment] = useState(comment);
  let [commentChanged, setCommentChanged] = useState(false);
  const user = useContext(UserContext);
  function handleShowEditorButton() {
    setShowEditor(!showEditor);
  }
  function handleCommentChange(evt) {
    setComment(evt.target.value);
    setCommentChanged(true);
  }
  async function handleCommentSubmit(evt) {
    evt.preventDefault();
    await sendFormData(evt, evt.target);
    setCommentChanged(false);
    setShowEditor(false);
    comment = newComment;
  }

  return (
    <>
      {user.admin && (
        <div>
          <div className="flex">
            <button
              className="p-1 rounded-full bg-orange-500 p-1 w-6 h-6 text-white leading-3"
              onClick={handleShowEditorButton}
              title="Kommentar hinzufügen/bearbeiten"
            >
              <ChatBubbleBottomCenterIcon className="h-4 w-4" />
            </button>
            {showEditor && (
              <form
                action={`/api/admin/event/${slot_id}/comment`}
                method="post"
                className="w-full"
                onSubmit={handleCommentSubmit}
              >
                <textarea
                  name="comment"
                  value={newComment || ""}
                  placeholder="Kommentar zur Buchung"
                  className="w-5/6"
                  onChange={handleCommentChange}
                ></textarea>
                {commentChanged && (
                  <button
                    type="submit"
                    className="rounded-full bg-orange-500 p-1 w-6 h-6 text-white leading-3"
                  >
                    <CheckIcon />
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
      )}
      {!showEditor && <p className="italic px-2">{newComment}</p>}
    </>
  );
}
function Booking({ booking }) {
  const user = useContext(UserContext);
  return (
    <li
      className="text-sm flex group/booking"
      title={`Buchung am ${new Date(booking.booking_time).toLocaleString()}`}
    >
      <a href={"mailto:" + booking.booker_email} className="font-medium mr-2">
        {booking.booker_name}
      </a>

      <span className="italic">{booking.comment} </span>
      <span className="font-thin">{`(${booking.attendees_count} P)`}</span>
      {user.admin && (
        <a
          href={`/cancel.html?cancel_token=${booking.cancel_token}`}
          className="hidden group-hover/booking:inline-flex items-center px-3"
          title={`Buchung von ${booking.booker_name} stornieren`}
        >
          <TrashIcon className="h-3 w-3" />
        </a>
      )}
    </li>
  );
}

function StaffAssignement({ timeslot, availableStaff }) {
  let [assignedStaff, setStaff] = useState(timeslot.assigned_staff);
  let [staffChanged, setStaffChanged] = useState(false);
  const user = useContext(UserContext);
  async function handleDelete(evt, member) {
    evt.preventDefault();
    let resp = await fetch(
      `/api/admin/timeslot/${timeslot.slot_id}/staff/${member}`,
      { method: "DELETE" }
    );
    if (resp.ok) {
      setStaff(assignedStaff.filter((m) => m !== member));
    }
  }
  async function handleStaffAssignement(evt) {
    evt.preventDefault();
    let resp = await postFormdata(evt.target);
    if (resp.ok) {
      setStaff([...assignedStaff, new FormData(evt.target).get("staff_name")]);
      setStaffChanged(false);
    }
    //changed = false;
  }
  return (
    <>
      {assignedStaff.map((member) => (
        <span
          key={member.staff_name}
          className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
        >
          <span>{member}</span>
          {user.admin && (
            <button
              type="button"
              className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
              onClick={(evt) => handleDelete(evt, member)}
            >
              <span className="sr-only">Remove</span>
              <svg
                viewBox="0 0 14 14"
                className="h-3.5 w-3.5 stroke-gray-600/50 group-hover:stroke-gray-600/75"
              >
                <path d="M4 4l6 6m0-6l-6 6" />
              </svg>
              <span className="absolute -inset-1"></span>
            </button>
          )}
        </span>
      ))}
      {user.admin && (
        <div>
          <form
            action="/api/admin/assign_staff"
            method="post"
            className="flex"
            onSubmit={handleStaffAssignement}
          >
            <select
              name="staff_name"
              className="bg-stone-200 mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
              onChange={(evt) => setStaffChanged(true)}
              defaultValue=""
            >
              <option value="">---</option>
              {availableStaff &&
                availableStaff
                  .filter(
                    (member) => !assignedStaff.includes(member.staff_name)
                  )
                  .map((member) => (
                    <option value={member.staff_name} key={member.staff_name}>
                      {member.staff_name}
                    </option>
                  ))}
            </select>
            <input
              type="hidden"
              value={timeslot.slot_id}
              name="event_id"
              className="rounded-full bg-orange-500 p-1 w-6 h-6"
            />
            {staffChanged && (
              <button
                type="submit"
                className="rounded-full bg-orange-500 p-1 w-6 h-6 text-white leading-3"
              >
                <CheckIcon />
              </button>
            )}
          </form>
        </div>
      )}
    </>
  );
}

function Ressource({ ressource, staff, deleteRessource, updateTimeslot }) {
  const [active, setActive] = useState(false);
  const user = useContext(UserContext);
  const { project } = useOutletContext();
  const queryClient = useQueryClient();
  function handleDeleteTimeslot() {
    if (timeslot.bookings[0]) {
      if (
        !window.confirm(
          `Achtung! Für diesen Termin gibt es ${ressource.bookings.length} Buchungen. Soll der Termin trotzdem gelöscht werden?`
        )
      ) {
        return;
      }
    }
    deleteRessource(ressource.slot_id);
  }
  async function addBooking(data) {
    const bookingData = { ...data, starttime: ressource.start };
    let resp = await fetch(`/api/admin/booking/${project.id}`, {
      method: "post",
      body: JSON.stringify(bookingData),
      headers: {
        "Content-Type": "application/json",
      },
    });
    //updateTimeslots();
    queryClient.invalidateQueries({ queryKey: ["timeslotsData", project.id] });
    return resp;
  }
  function handleAddBooking(evt) {
    NiceModal.show(BookingForm, {
      variants: ressource.bookings[0]
        ? [ressource.bookings[0].ressource_name]
        : ressource.available_ressources,
      returnFormData: addBooking,
    });
  }
  function handleEditTimeslot(evt) {
    updateTimeslot(ressource);
  }
  let timeslot = ressource;
  timeslot.start = new Date(timeslot.start);
  return (
    <>
      <li
        id={timeslot.start.toISOString()}
        onClick={() => setActive(!active)}
        className="group"
      >
        <div>
          <figure
            className={
              "bg-gray-100 rounded-xl pt-4 my-2 print:border + {'bg-stone-200 pt-1': !timeslot.sum, 'border border-orange-500': selected}"
            }
          >
            <div className="flex px-4 pb-1">
              <div className="pr-4">
                <h4 className="text-xl" title={timeslot.start.toLocaleString()}>
                  {`${timeslot.start.toLocaleString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`}
                </h4>
              </div>
              <div className="flex-auto">
                <div x-if="timeslot.sum">
                  <ul>
                    {timeslot.bookings.map(
                      (booking) =>
                        booking && (
                          <Booking booking={booking} key={booking.booking_id} />
                        )
                    )}
                  </ul>
                </div>
                {user.admin && (
                  <button onClick={handleAddBooking} className="text-xs">
                    Buchung hinzufügen
                  </button>
                )}
              </div>
              <div>
                {user.admin && (
                  <div className="hidden sm:flex space-x-1">
                    <button
                      type="button"
                      onClick={() => handleEditTimeslot(ressource)}
                      className="hidden group-hover:inline-flex grow-0 items-center gap-x-1.5 rounded-md bg-white ring-1 ring-inset ring-orange-500 px-2.5 py-1 text-sm text-orange-500 shadow-sm hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteTimeslot}
                      className="hidden group-hover:inline-flex items-center gap-x-1.5 rounded-md bg-white ring-1 ring-inset ring-red-500 px-2.5 py-1 text-sm text-red-500 shadow-sm hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    >
                      <TrashIcon className="w-3 h-3" />
                      Löschen
                    </button>
                  </div>
                )}
                <div className="flex">
                  <div className={user.admin && "group-hover:hidden"}>
                    {timeslot.bookings[0] && (
                      <span className="italic text-sm">
                        {timeslot.bookings[0].ressource_name}
                      </span>
                    )}
                    {!timeslot.bookings[0] && (
                      <span className="font-thin italic text-sm">
                        {timeslot.available_ressources.join(", ")}
                      </span>
                    )}

                    {timeslot.internal && (
                      <span className="font-thin italic text-sm">
                        {" "}
                        (intern)
                      </span>
                    )}
                  </div>
                  {user.admin && (
                    <Menu
                      as="div"
                      className="sm:hidden relative inline-block text-left"
                    >
                      <div>
                        <MenuButton className="flex items-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                          <span className="sr-only">Open options</span>
                          <EllipsisVerticalIcon
                            aria-hidden="true"
                            className="size-5"
                          />
                        </MenuButton>
                      </div>

                      <MenuItems
                        transition
                        className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        <div className="py-1">
                          <MenuItem>
                            <button
                              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                              onClick={() => handleEditTimeslot(ressource)}
                            >
                              Bearbeiten
                            </button>
                          </MenuItem>
                          <MenuItem>
                            <a
                              href="#"
                              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                            >
                              Löschen
                            </a>
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </Menu>
                  )}
                </div>
              </div>
            </div>
            <div className="flex rounded-b-xl px-4 bg-stone-200">
              <div className="flex flex-wrap flex-auto">
                <div className="flex flex-auto items-center">
                  <RessourceComment
                    slot_id={ressource.slot_id}
                    comment={timeslot.comment}
                  />
                </div>
                <StaffAssignement timeslot={timeslot} availableStaff={staff} />
              </div>
            </div>
          </figure>
        </div>
      </li>
    </>
  );
}

function RessourceListGroup({
  ressources,
  staff,
  deleteRessource,
  updateTimeslot,
  variants,
}) {
  return (
    <section>
      <h3 className="text-2xl mt-8 sticky top-0 pb-2 bg-white/90">
        {ressources[0].start.toLocaleString(undefined, {
          weekday: "short",
          year: "numeric",
          month: "numeric",
          day: "numeric",
        })}
      </h3>
      <ol>
        {ressources.map((ressource) => (
          <Ressource
            ressource={ressource}
            staff={staff}
            deleteRessource={deleteRessource}
            key={ressource.slot_id}
            updateTimeslot={updateTimeslot}
            variants={variants}
          />
        ))}
      </ol>
    </section>
  );
}

function RessourceList() {
  //const [ressources, setRessources] = useState([]);
  const [filter, setFilter] = useState({});
  //const [availableVariants, setAvailableVariants] = useState([]);
  const user = useContext(UserContext);
  const { staff, project } = useOutletContext();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  async function updateTimeslots() {
    queryClient.invalidateQueries({ queryKey: ["timeslotsData", project?.id] });
  }

  const timeslotsQuery = useQuery({
    queryKey: ["timeslotsData", project?.id],
    queryFn: async () => {
      const resp = await fetch(`/api/admin/events/${project.id}`);
      if (!resp.ok) {
        throw new Error("Failed to fetch timeslots data");
      }
      return resp.json();
    },
    placeholderData: [],
  });

  const variantsQuery = useQuery({
    queryKey: ["variantsData", project?.id],
    queryFn: async () => {
      const resp = await fetch(`/api/admin/variants/${project.id}`);
      if (!resp.ok) {
        throw new Error("Failed to fetch variants data");
      }
      return resp.json();
    },
    placeholderData: [],
  });

  function filterRessources(ressources, myfilter) {
    return ressources.filter(
      (ressource) =>
        (!myfilter?.team ||
          myfilter.team.length == 0 ||
          ressource.assigned_staff.some((e) => myfilter.team.includes(e))) &&
        (!myfilter?.variants ||
          myfilter.variants.length == 0 ||
          ressource.available_ressources.some((e) =>
            myfilter.variants.includes(e)
          )) &&
        (!myfilter?.bookings ||
          myfilter.bookings.length == 0 ||
          (myfilter.bookings.includes("withBookings") &&
            ressource.bookings[0]) ||
          (myfilter.bookings.includes("withoutBookings") &&
            ressource.bookings[0] === null))
    );
  }

  async function deleteRessource(slot_id) {
    const resp = await deleteTimeslot(slot_id);
    if (resp.ok) {
      queryClient.setQueryData(["timeslotsData", project?.id], (oldData) =>
        oldData.filter((ressource) => ressource.slot_id !== slot_id)
      );
    }
  }
  function addRessourceToList(newRessource) {
    queryClient.setQueryData(["timeslotsData", project?.id], (oldData) => {
      if (oldData)
        return [...oldData, newRessource].sort((a, b) => a.start - b.start);
    });
  }

  function handleAddTimeslot(ressource) {
    // TODO
    NiceModal.show(AddTimeslotDialog, {
      ressource: ressource,
      variants: variantsQuery.data,
      project: project,
      addRessourceToState: addRessourceToList,
    });
  }

  let ressourcesGrouped = {};
  if (timeslotsQuery.data?.length) {
    // Group ressources by date
    ressourcesGrouped = filterRessources(timeslotsQuery.data, filter).reduce(
      (groups, item) => {
        item.start = new Date(item.start);
        const group = groups[item.start.toLocaleDateString()] || [];
        group.push(item);
        groups[item.start.toLocaleDateString()] = group;
        return groups;
      },
      {}
    );
  }
  return (
    <>
      <div className="flex justify-between items-end">
        {user.admin && (
          <button
            type="button"
            onClick={() => handleAddTimeslot()}
            className="flex items-center gap-x-1.5 rounded-md bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <CalendarIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
            Termin hinzufügen
          </button>
        )}
        {variantsQuery.data && (
          <RessourceFilter
            team={staff}
            pushFilterUpdate={setFilter}
            availableVariants={variantsQuery.data}
          />
        )}
      </div>
      {ressourcesGrouped.length && <EmptyTimeslots />}

      {Object.keys(ressourcesGrouped).map((date) => (
        <RessourceListGroup
          ressources={ressourcesGrouped[date]}
          staff={staff}
          deleteRessource={deleteRessource}
          updateTimeslot={handleAddTimeslot}
          key={date}
        />
      ))}
    </>
  );
}

function Dashboard() {
  let { user, logout } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);

  const queryClient = useQueryClient();
  const { data: staff } = useQuery({
    queryKey: ["teamData"],
    queryFn: getTeam,
    placeholderData: [],
    refetchOnWindowFocus: false,
    skip: !user,
  });

  const projectsQuery = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const resp = await fetch("/api/admin/projects");
      if (!resp.ok) {
        throw new Error("Failed to fetch projects data");
      }
      return resp.json();
    },
    placeholderData: [],
  });

  useEffect(() => {
    if (projectsQuery.data.length) {
      let initialProject =
        projectsQuery.data.find((p) => p.last_used) || projectsQuery.data[0];
      setSelectedProject(initialProject);
    }
  }, [projectsQuery.data]);

  function handleLogout(evt) {
    /*document.cookie =
      "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    window.location.reload();*/
    logout();
  }
  function handleChangePasswword() {
    NiceModal.show(PasswordEditor, { user: user });
  }
  const userNavigation = [
    //  { name: "Your Profile", href: "#" },
    //  { name: "Settings", href: "#" },
    { name: "Abmelden", href: "#", onclick: handleLogout },
    { name: "Passwort ändern", href: "#", onclick: handleChangePasswword },
  ];

  if (!user || !projectsQuery.data.length) {
    return (
      <div>
        Keine Projekte gefunden. Bitte wenden Sie sich an den Administrator.
      </div>
    );
  }
  return (
    <UserContext.Provider value={user}>
      {/*
        This example requires updating your div:

        ```
        <html className="h-full">
        <body className="h-full">
        ```
      */}
      <div className="min-h-full">
        <Disclosure as="nav" className="border-b border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 justify-between">
              <div className="flex">
                <div className="block sm:hidden">
                  {projectsQuery.data.length > 1 && (
                    <ProjectsSelector
                      user={user}
                      projects={projectsQuery.data}
                      selectedProject={selectedProject}
                      setSelectedProject={setSelectedProject}
                    />
                  )}
                </div>

                {/*<div className="flex flex-shrink-0 items-center">
                  <img
                    alt="bookingal"
                    src=""
                    className="block h-8 w-auto lg:hidden"
                  />
                  <img
                    alt="bookingal"
                    src=""
                    className="hidden h-8 w-auto lg:block"
                  />
                </div>*/}
                <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                  {navigation.map((item) =>
                    !user.admin && item.admin ? null : (
                      <a
                        key={item.name}
                        href={item.href}
                        aria-current={item.current ? "page" : undefined}
                        className={classNames(
                          item.current
                            ? "border-indigo-500 text-gray-900"
                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                          "inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                        )}
                      >
                        {item.name}
                      </a>
                    )
                  )}
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:items-center">
                {user.admin && projectsQuery.data.length === 1 ? (
                  <button
                    type="button"
                    className="rounded bg-white mx-4 px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    onClick={() => {
                      NiceModal.show(ProjectEditor, {
                        queryClient: queryClient,
                      });
                    }}
                  >
                    Projekt hinzufügen
                  </button>
                ) : (
                  <ProjectsSelector
                    user={user}
                    projects={projectsQuery.data}
                    selectedProject={selectedProject}
                    setSelectedProject={setSelectedProject}
                  />
                )}
                {/*<button
                  type="button"
                  className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="h-6 w-6" />
                </button>*/}

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <MenuButton className="relative flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      {/*<img
                        alt=""
                        src={user.imageUrl}
                        className="h-8 w-8 rounded-full"
                      />*/}
                      {user.username}
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="-mr-1 size-5 text-gray-400"
                      />
                    </MenuButton>
                  </div>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    {userNavigation.map((item) => (
                      <MenuItem
                        key={item.name}
                        as="button"
                        href={item.href}
                        onClick={item.onclick}
                        className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
              <div className="-mr-2 flex items-center sm:hidden">
                {/* Mobile menu button */}
                <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon
                    aria-hidden="true"
                    className="block h-6 w-6 group-data-[open]:hidden"
                  />
                  <XMarkIcon
                    aria-hidden="true"
                    className="hidden h-6 w-6 group-data-[open]:block"
                  />
                </DisclosureButton>
              </div>
            </div>
          </div>

          <DisclosurePanel className="sm:hidden">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) =>
                !user.admin && item.admin ? null : (
                  <DisclosureButton
                    key={item.name}
                    as={NavLink}
                    to={item.href}
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </DisclosureButton>
                )
              )}
            </div>
            <div className="border-t border-gray-200 pb-3 pt-4">
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  {/*<img alt="" src={user.imageUrl} className="h-10 w-10 rounded-full" />*/}
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user.name}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {user.email}
                  </div>
                </div>
                {/*<button
                  type="button"
                  className="relative ml-auto flex-shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="h-6 w-6" />
                </button>*/}
              </div>
              <div className="mt-3 space-y-1">
                {userNavigation.map((item) => (
                  <DisclosureButton
                    key={item.name}
                    as="button"
                    href={item.href}
                    onClick={item.onclick}
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  >
                    {item.name}
                  </DisclosureButton>
                ))}
              </div>
            </div>
          </DisclosurePanel>
        </Disclosure>

        <div className="py-2">
          <header>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {/*<h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
                Dashboard
              </h1>*/}
            </div>
          </header>
          <main>
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
              <Outlet context={{ staff: staff, project: selectedProject }} />
            </div>
          </main>
        </div>
      </div>
    </UserContext.Provider>
  );
}

const getUserData = async () => {
  const response = fetch("/api/users/me/", {
    credentials: "same-origin",
  });
  return response;
  if (response.status === 401) {
    console.error("Unauthorized access, redirecting to login");
    return null;
  } else if (!response.ok) {
    throw new Error("Failed to fetch user data");
  }
  return response;
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route index element={<Navigate replace to="/admin/" />} />
      <Route path="registration" element={<Registration />} />
      <Route path="password-reset" element={<PasswordReset />} />
      <Route
        element={<AuthLayout />}
        loader={() => {
          return {
            userPromise: getUserData(),
          };
        }}
      >
        <Route path="login" element={<LoginScreen />} />
        <Route path="admin" element={<ProtectedLayout />}>
          <Route element={<Dashboard />}>
            <Route index element={<RessourceList />} />
            <Route path="settings" element={<Settings />} />
            <Route path="team" element={<UserList />} />
            <Route path="help" element={<Help />} />
          </Route>
        </Route>
      </Route>
    </>
  )
);
